import React, { useState, useEffect } from 'react';
import { Save, Printer, Loader2, Check, AlertCircle } from 'lucide-react';
import printProfileService from '../services/printProfile';

const PrintSettings = () => {
  const [profile, setProfile] = useState({
    header1: '', header2: '', header3: '',
    footer1: '', footer2: '', footer3: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await printProfileService.getProfile();
        setProfile({
          header1: data.header1 || '',
          header2: data.header2 || '',
          header3: data.header3 || '',
          footer1: data.footer1 || '',
          footer2: data.footer2 || '',
          footer3: data.footer3 || ''
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await printProfileService.updateProfile(profile);
      setMessage('Profil cetak berhasil disimpan');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex flex-col items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /><span className="mt-3 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Master Cetak Struk</h2>
          <p className="text-sm text-gray-500 mt-1">Atur teks header & footer yang muncul di struk POS</p>
        </div>
      </div>

      {message && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center space-x-2 animate-slide-up">
          <Check className="w-4 h-4 text-green-500" /><p className="text-sm text-green-700">{message}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center space-x-2">
          <AlertCircle className="w-4 h-4 text-red-500" /><p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Preview */}
      <div className="card p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
          <Printer className="w-4 h-4 text-amber-500" /><span>Pratinjau Struk</span>
        </h3>
        <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-4 font-mono text-[10px] leading-relaxed max-w-[200px] mx-auto">
          <div className="text-center font-bold text-xs">{profile.header1 || '(header1)'}</div>
          <div className="text-center">{profile.header2 || '(header2)'}</div>
          <div className="text-center">{profile.header3 || '(header3)'}</div>
          <div className="border-t border-dashed border-gray-300 my-2" />
          <div className="text-center">Struk Belanja</div>
          <div className="border-t border-dashed border-gray-300 my-2" />
          <div>Item 1 x 1    Rp 10.000</div>
          <div>Item 2 x 2    Rp 20.000</div>
          <div className="border-t border-dashed border-gray-300 my-2" />
          <div className="flex justify-between"><span>Total</span><span>Rp 30.000</span></div>
          <div className="border-t border-dashed border-gray-300 my-2" />
          <div className="text-center">{profile.footer1 || '(footer1)'}</div>
          <div className="text-center">{profile.footer2 || '(footer2)'}</div>
          <div className="text-center">{profile.footer3 || '(footer3)'}</div>
        </div>
      </div>

      {/* Header Input */}
      <div className="card p-6 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Header Struk</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Baris 1 (judul toko — bold)</label>
            <input type="text" value={profile.header1} onChange={e => setProfile(p => ({ ...p, header1: e.target.value }))}
              className="input-field w-full text-sm" placeholder="Nama Toko" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Baris 2 (alamat)</label>
            <input type="text" value={profile.header2} onChange={e => setProfile(p => ({ ...p, header2: e.target.value }))}
              className="input-field w-full text-sm" placeholder="Jl. Contoh No. 123" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Baris 3 (info tambahan — telp, NPWP, dll)</label>
            <input type="text" value={profile.header3} onChange={e => setProfile(p => ({ ...p, header3: e.target.value }))}
              className="input-field w-full text-sm" placeholder="Telp: 0812-3456-7890" />
          </div>
        </div>
      </div>

      {/* Footer Input */}
      <div className="card p-6 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Footer Struk</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Baris 1 (terima kasih)</label>
            <input type="text" value={profile.footer1} onChange={e => setProfile(p => ({ ...p, footer1: e.target.value }))}
              className="input-field w-full text-sm" placeholder="Terima Kasih" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Baris 2 (info tambahan)</label>
            <input type="text" value={profile.footer2} onChange={e => setProfile(p => ({ ...p, footer2: e.target.value }))}
              className="input-field w-full text-sm" placeholder="Barang yang sudah dibeli tidak dapat ditukar" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Baris 3 (info tambahan)</label>
            <input type="text" value={profile.footer3} onChange={e => setProfile(p => ({ ...p, footer3: e.target.value }))}
              className="input-field w-full text-sm" placeholder="www.tokoanda.com" />
          </div>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving}
        className="btn-primary">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Menyimpan...</span></>
          : <><Save className="w-4 h-4" /><span>Simpan Profil Cetak</span></>}
      </button>
    </div>
  );
};

export default PrintSettings;
